import companyMixin from "@/v2/mixins/companies.js";
import httpMixin from "@/v2/mixins/http.js";
import mapperMixin from "@/v2/mixins/mapper.js";
import mediaMixin from "@/v2/mixins/medias.js";
import productMixin from "@/v2/mixins/products.js";
import taxClassMixin from "@/v2/mixins/tax-classes.js";
import warehouseMixin from "@/v2/mixins/warehouses.js";
import {
    DocumentFactory,
    DocumentLineItemFactory,
    DocumentLineItemMapperParameter,
    DocumentMapperParameter,
} from "@/v2/models/documents.js";
import moment from "moment";

export default {
    mixins: [mapperMixin, httpMixin, productMixin, companyMixin, warehouseMixin, mediaMixin, taxClassMixin],
    computed: {
        documentsUrl: {
            get() {
                return window.bo.documentsUrl;
            },
        },
        documentLineItemsUrl: {
            get() {
                return window.bo.documentLineItemsUrl;
            },
        },
        reuploadAidocAttachmentUrl: {
            get() {
                return window.bo.reuploadAidocAttachmentUrl;
            },
        },
    },
    methods: {
        _documentMapper(document) {
            // Get from api and convert to frontend model
            return {
                id: document.id,
                id_link: document.id_link,
                date: new Date(document.date),
                number: document.number,
                temporary_document_number: document.temporary_document_number,
                external_number: document.external_number,
                split_group: document.split_group,
                allow_multiple_split_groups: document.allow_multiple_split_groups,
                total: document.total,
                billing_address: document.billing_address,
                shipping_address: document.shipping_address,
                company: document.company,
                line_items: document.line_items,
                term: document.term,
                type: document.type,
                outstanding: Number.parseFloat(document.outstanding),
                unapplied_amount: Number.parseFloat(document.unapplied_amount),
                amount: Number.parseFloat(document.amount),
                status: document.status,
                credit_approval_status: document.credit_approval_status,
                credit_approval_workflow: document.credit_approval_workflow,
                payment_amount: Number.parseFloat(document.payment_amount),
                payment_remark: document.payment_remark,
                remark: document.remark,
                created_by: document.created_by,
                document_paying_fors: document.document_paying_fors,
                document_paid_withs: document.document_paid_withs,
                document_paying_withs: document.document_paying_withs,
                document_used_to_pay: document.document_used_to_pay,
                paid: Number.parseFloat(document.paid),
                used: Number.parseFloat(document.used),
                discount: Number.parseFloat(document.discount),
                payment_method: document.payment_method,
                payment_method_label: document.payment_method_label,
                cheque_date: new Date(document.cheque_date),
                cheque_number: document.cheque_number,
                custom_fields: document.custom_fields,
                created_at: new Date(document.created_at),
                updated_at: new Date(document.updated_at),
                date_valid_to: document.date_valid_to ? new Date(document.date_valid_to) : null,
                custom_field_attachments: document.custom_field_attachments,
                lending_program_name: document.lending_program_name,
                total_points: document.total_points,
                agent_code: document.agent_code,
                agent_name: document.agent_name,
                transfer_status: document.transfer_status,
                delivery_date: document.delivery_date ? new Date(document.delivery_date) : null,
                shipping_line: document.shipping_line,
                coupon_logs: document.coupon_logs,
                discounts: document.discounts,
                default_incoterm_type: document.default_incoterm_type,
                default_incoterm_from: document.default_incoterm_from,
                default_incoterm_to: document.default_incoterm_to,
                credit_term_code: document.credit_term_code,
                error_user_credit_limit_exceeded_warn_message: document.error_user_credit_limit_exceeded_warn_message,
                packed_at: document.packed_at ? new Date(moment.utc(document.packed_at).local()) : null,
                price_approval_status: document.price_approval_status,
                price_approval_status_slug: document.price_approval_status_slug,
                currency: document.currency,
                currencySymbol: document.currencySymbol,
                errors: document.errors,
                due_date: new Date(document.due_date),
                overdue_date: new Date(document.overdue_date),
                last_payment_date: document.last_payment_date,
                payment_status: document.payment_status,
                payment_timeline: document.payment_timeline,
                delivered_at: document.delivered_at ? new Date(moment.utc(document.delivered_at).local()) : null,
                delivered_by: document.delivered_by,
                delivered_by_name: document.delivered_by_name,
                is_delivered: document.is_delivered,
                phone_verification_status: document.phone_verification_status,
                epod_bypass_remark: document.epod_bypass_remark,
                received_at: document.received_at ? new Date(moment.utc(document.received_at).local()) : null,
                received_by: document.received_by,
                received_by_identification_number: document.received_by_identification_number,
                received_by_phone: document.received_by_phone,
                received_at_longitude: document.received_at_longitude,
                received_at_latitude: document.received_at_latitude,
                proof_of_delivery: document.proof_of_delivery,
                received_by_e_signature: document.received_by_e_signature,
                e_invoice_uuid: document.e_invoice_uuid,
                e_invoice_code: document.e_invoice_code,
                merged_documents: document.merged_documents ?? [],
                e_invoices: document.e_invoices,
                integration: document.integration,
                external_account_code: document.external_account_code,
                reference_documents: document.reference_documents,
                document_referenced: document.document_referenced,
                document_error_messages: document.document_error_messages,
                aidoc_template: document.aidoc_template,
                aidoc_attachment: document.aidoc_attachment,
                document_terms_and_conditions: document.document_terms_and_conditions,
                site_name: document.site_name,
                purchase_limit_approval_status: document.purchase_limit_approval_status,
                parcels: document.parcels,
            };
        },

        _documentLineItemMapper(documentLineItem) {
            return {
                id: documentLineItem.id,
                sequence: documentLineItem.sequence,
                bo_can_delete: documentLineItem.bo_can_delete,
                show_action_menu: documentLineItem.show_action_menu,
                subtotal: Number.parseFloat(documentLineItem.subtotal),
                subtotal_tax: Number.parseFloat(documentLineItem.subtotal_tax),
                total: Number.parseFloat(documentLineItem.total),
                total_tax: Number.parseFloat(documentLineItem.total_tax),
                price: Number.parseFloat(documentLineItem.price),
                quantity: documentLineItem.quantity,
                untransferred_quantity: documentLineItem.untransferred_quantity,
                product_name: documentLineItem.product_name,
                product_sku: documentLineItem.product?.parent?.sku
                    ? documentLineItem.product.parent.sku
                    : documentLineItem.product_sku,
                variations: documentLineItem?.product?.parent?.variations,
                product_id: documentLineItem.product_id,
                product_uom: documentLineItem.product ? documentLineItem.product.uom : null,
                product: documentLineItem.product,
                discounts: documentLineItem.discounts,
                total_points: documentLineItem.total_points,
                composite_parent: documentLineItem.composite_parent,
                composite_children: documentLineItem.composite_children,
                remark: documentLineItem.remark,
                from_location: documentLineItem.from_location,
                to_location: documentLineItem.to_location,
                related_document_transferred_to: documentLineItem.transferred_to,
                transferred_to: documentLineItem.transferred_to,
                transferred_from: documentLineItem.transferred_from,
                return_reason: documentLineItem.return_reason,
                batch: documentLineItem.batch,
                document_number: documentLineItem.document_number,
                document_type: documentLineItem.document_type,
                document_status: documentLineItem.document_status,
                admin_portal_document_id_link: documentLineItem.admin_portal_document_id_link,
                serial_number: documentLineItem.serial_numbers,
                untransferred_serial_number: documentLineItem.untransferred_serial_number,
                price_before_discount: documentLineItem.subtotal / documentLineItem.quantity,
                created_at: new Date(documentLineItem.created_at),
                tax_class: documentLineItem.tax_class,
                shipping_address: documentLineItem.shipping_address,
                discounts: documentLineItem.discounts,
                transfer_status: documentLineItem.transfer_status,
                meta: documentLineItem.meta,
                document_date: documentLineItem.document_date ? new Date(documentLineItem.document_date) : null,
                document_id_link: documentLineItem.document_id_link,
                default_incoterm_type: documentLineItem.default_incoterm_type,
                default_incoterm_from: documentLineItem.default_incoterm_from,
                default_incoterm_to: documentLineItem.default_incoterm_to,
                display_subtotal: documentLineItem.display_subtotal,
                display_total: documentLineItem.display_total,
                price_approval_status: documentLineItem.price_approval_status ?? "Not Required",
                purchase_limit_approval_status: documentLineItem.purchase_limit_approval_status ?? "not-required",
                price_approval_status_slug: documentLineItem.price_approval_status_slug ?? "not-required",
                min_price: documentLineItem.min_price ? Number.parseFloat(documentLineItem.min_price) : null,
                system_price: documentLineItem.system_price ? Number.parseFloat(documentLineItem.system_price) : null,
                external_price: documentLineItem.external_price ?? null,
                integration: documentLineItem.integration,
                external_sku: documentLineItem.external_sku,
                external_uom: documentLineItem.external_uom,
                total_received_quantity: documentLineItem.total_received_quantity,
                total_shipped_quantity: documentLineItem.total_shipped_quantity,
            };
        },

        _documentLineItemTransferMapper(data) {
            return {
                id: data.id,
                line_item: data.line_item,
                document_id: data.document_id,
                document_number: data.document_number,
            };
        },

        parseDocument(document) {
            return DocumentFactory.construct(this.createMapperParameter(new DocumentMapperParameter()), document);
        },

        parseDocuments(documents) {
            let parsedDocuments = [];

            documents.forEach((documentData) => {
                const document = this.parseDocument(documentData);
                parsedDocuments.push(document);
            });

            return parsedDocuments;
        },

        parseDocumentLineItem(documentLineItem) {
            return DocumentLineItemFactory.construct(
                this.createMapperParameter(new DocumentLineItemMapperParameter()),
                documentLineItem
            );
        },

        parseDocumentLineItems(documentLineItems) {
            let parsedDocumentLineItems = [];

            documentLineItems.forEach((documentLineItemData) => {
                const documentLineItem = this.parseDocumentLineItem(documentLineItemData);
                parsedDocumentLineItems.push(documentLineItem);
            });

            return parsedDocumentLineItems;
        },

        getDocument(id, payload, success, failure) {
            return this.getData(
                this.documentsUrl + "/" + id,
                payload,
                (response) => {
                    success(this.parseDocument(response.data));
                },
                failure
            );
        },

        getRelatedDocuments(id, payload, success, failure) {
            const toRow = (relatedDocument) => ({
                ...relatedDocument,
                admin_portal_document_id_link: relatedDocument.admin_portal_document_id_link?.link ?? null,
            });
            return this.getData(
                this.documentsUrl + "/" + id + "/related-documents",
                payload,
                (response) => {
                    success(
                        response.data.data.map(toRow),
                        response.data.meta,
                        (response.data.e_invoices ?? []).map(toRow)
                    );
                },
                failure
            );
        },

        getDocuments(payload, success, failure) {
            return this.getData(
                this.documentsUrl,
                payload,
                (response) => {
                    // Response.data.data is the data from the api
                    // Response.data.meta is the pagination
                    success(this.parseDocuments(response.data.data), response.data.meta);
                },
                failure
            );
        },

        getDocumentLineItems(payload, success, failure) {
            return this.getData(
                this.documentLineItemsUrl,
                payload,
                (response) => {
                    success(this.parseDocumentLineItems(response.data.data), response.data.meta);
                },
                failure
            );
        },

        getDocumentLineItem(payload, success, failure) {
            return this.getData(
                this.documentLineItemsUrl,
                payload,
                (response) => {
                    success(this.parseDocumentLineItem(response.data));
                },
                failure
            );
        },

        createDocument(payload, success, failure) {
            return this.postData(
                this.documentsUrl,
                payload,
                (response) => {
                    if (typeof response.data.split_order !== "undefined") {
                        success(response.data);
                    } else {
                        success(this.parseDocument(response.data));
                    }
                },
                failure
            );
        },

        updateDocument(id, payload, success, failure) {
            return this.putData(
                `${this.documentsUrl}/${id}`,
                payload,
                (response) => {
                    if (typeof response.data.split_order !== "undefined") {
                        success(response.data);
                    } else {
                        success(this.parseDocument(response.data));
                    }
                },
                failure
            );
        },

        reuploadAidocAttachment(id, payload, success, failure) {
            return this.putData(
                `${this.reuploadAidocAttachmentUrl}/${id}`,
                payload,
                (response) => {
                    success(this.parseDocument(response.data));
                },
                failure
            );
        },

        createOrUpdateDocument(id = null, payload, success, failure) {
            if (id == null) {
                return this.createDocument(payload, success, failure);
            } else {
                return this.updateDocument(id, payload, success, failure);
            }
        },

        deleteDocument(id, payload, success, failure) {
            return this.deleteData(
                `${this.documentsUrl}/${id}`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        deleteDocuments(payload, success, failure) {
            return this.postBulkData(
                this.documentsUrl,
                null,
                null,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        refreshDocument(id, documents, success, failure) {
            this.getDocument(
                id,
                this.documentsUrl,
                null,
                (document) => {
                    documents[id] = document;
                    if (success) success(document);
                },
                failure
            );
        },

        createPaymentDocument(url, payload, success, failure) {
            return this.postData(
                url,
                payload,
                (response) => {
                    success(this.parseDocument(response.data));
                },
                failure
            );
        },

        calculateShippingFee(url, payload, shippingMethodIds, success, failure) {
            if (shippingMethodIds instanceof Array) {
                shippingMethodIds = shippingMethodIds.join(",");
            }

            return this.postData(
                `${url}/${payload.id}/shipping-fee`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        getTradeAgreementPrice(url, payload, success, failure) {
            return this.postData(
                url,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        getSplitOrderCondition(url, payload, success, failure) {
            return this.postData(
                url,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        getReferenceInvoice(url, payload, success, failure) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(response.data.data, response.data.meta);
                },
                failure
            );
        },

        calculateShippingFeeForNewDocument(url, payload, shippingMethodIds, success, failure) {
            if (shippingMethodIds instanceof Array) {
                shippingMethodIds = shippingMethodIds.join(",");
            }

            return this.postDataWithoutAsync(
                `${url}/shipping-fee`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },
    },
};
